import { useEffect, useRef } from "react";
import { motion } from "./fm";

function roundedRect(w, h, r, step) {
  const pts = [];
  const line = (x1, y1, x2, y2) => {
    const d = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(1, Math.round(d / step));
    for (let i = 0; i < n; i++) {
      const t = i / n;
      pts.push([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t]);
    }
  };
  const arc = (cx, cy, a0, a1) => {
    const n = Math.max(2, Math.round((Math.abs(a1 - a0) * r) / step));
    for (let i = 0; i < n; i++) {
      const a = a0 + (a1 - a0) * (i / n);
      pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    }
  };
  line(r, 0, w - r, 0);
  arc(w - r, r, -Math.PI / 2, 0);
  line(w, r, w, h - r);
  arc(w - r, h - r, 0, Math.PI / 2);
  line(w - r, h, r, h);
  arc(r, h - r, Math.PI / 2, Math.PI);
  line(0, h - r, 0, r);
  arc(r, r, Math.PI, Math.PI * 1.5);
  return pts;
}

function rectOutline(x, y, w, h, step) {
  const pts = [];
  const seg = (x1, y1, x2, y2) => {
    const n = Math.max(1, Math.round(Math.hypot(x2 - x1, y2 - y1) / step));
    for (let i = 0; i < n; i++) {
      const t = i / n;
      pts.push([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t]);
    }
  };
  seg(x, y, x + w, y);
  seg(x + w, y, x + w, y + h);
  seg(x + w, y + h, x, y + h);
  seg(x, y + h, x, y);
  return pts;
}

export default function GhostCard({ dormant, hovered }) {
  const canvasRef = useRef(null);
  const state = useRef({ dormant, hovered });
  state.current = { dormant, hovered };

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let w = 0;
    let h = 0;
    let gather = 0.12;
    let t = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: -9999, y: -9999 };
    let particles = [];

    const build = () => {
      const rect = host.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      if (w < 4 || h < 4) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const edge = roundedRect(w, h, 28, 15);
      const inner = [
        ...rectOutline(w * 0.09, h * 0.4, w * 0.82, 58, 26),
        ...rectOutline(w * 0.09, h * 0.55, w * 0.82, 58, 26),
      ];
      const rows = [];
      const rowLine = (cy, frac, step) => {
        const x0 = w * (0.5 - frac / 2);
        const n = Math.max(2, Math.round((w * frac) / step));
        for (let i = 0; i <= n; i++) rows.push([x0 + (w * frac * i) / n, cy]);
      };
      rowLine(h * 0.24, 0.46, 20);
      rowLine(h * 0.3, 0.3, 20);
      rowLine(h * 0.88, 0.34, 22);

      const homes = [
        ...edge.map((p) => ({ p, edge: true })),
        ...inner.map((p) => ({ p, edge: false })),
        ...rows.map((p) => ({ p, edge: false })),
      ];

      particles = homes.map(({ p, edge: isEdge }) => {
        const spread = 150;
        return {
          hx: p[0],
          hy: p[1],
          x: p[0] + (Math.random() - 0.5) * spread,
          y: p[1] + (Math.random() - 0.5) * spread,
          px: Math.random() * Math.PI * 2,
          py: Math.random() * Math.PI * 2,
          sx: 0.12 + Math.random() * 0.22,
          sy: 0.12 + Math.random() * 0.22,
          ax: 34 + Math.random() * 78,
          ay: 26 + Math.random() * 62,
          r: Math.random() * 1.3 + 0.7,
          hot: Math.random() < 0.14,
          edge: isEdge,
        };
      });
    };

    const frame = () => {
      t += 0.016;
      const { dormant: isDormant, hovered: isHovered } = state.current;
      const target = !isDormant ? 1 : isHovered ? 0.82 : 0.1;
      gather += (target - gather) * 0.055;

      ctx.clearRect(0, 0, w, h);
      const scatter = 1 - gather;

      for (const p of particles) {
        const tx =
          p.hx + Math.sin(t * p.sx * 2.2 + p.px) * p.ax * scatter;
        const ty =
          p.hy + Math.cos(t * p.sy * 2.2 + p.py) * p.ay * scatter;
        const ease = 0.022 + gather * 0.075;
        p.x += (tx - p.x) * ease;
        p.y += (ty - p.y) * ease;

        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const d = Math.hypot(dx, dy);
        if (d < 150 && d > 0.01) {
          const pull = (1 - d / 150) * 0.9 * scatter;
          p.x += (dx / d) * pull;
          p.y += (dy / d) * pull;
        }
      }

      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (
          let j = i + 1;
          j < Math.min(i + 7, particles.length);
          j++
        ) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          const max = 96;
          if (d < max) {
            const outline =
              a.edge && b.edge && j === i + 1 ? 0.55 : 0.22;
            ctx.strokeStyle = `rgba(91,140,255,${
              (1 - d / max) * outline * (0.35 + gather * 0.65)
            })`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        const mdx = mouse.x - a.x;
        const mdy = mouse.y - a.y;
        const md = Math.hypot(mdx, mdy);
        if (md < 170) {
          ctx.strokeStyle = `rgba(255,210,31,${(1 - md / 170) * 0.4})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }

      for (const p of particles) {
        const glow = p.hot ? 0.95 : 0.55 + gather * 0.4;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + gather * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = p.hot
          ? `rgba(255,210,31,${glow})`
          : `rgba(147,180,255,${glow})`;
        ctx.fill();
      }

      if (gather > 0.4) {
        const a = (gather - 0.4) / 0.6;
        ctx.fillStyle = `rgba(255,255,255,${a * 0.05})`;
        ctx.beginPath();
        const r = 28;
        ctx.moveTo(r, 0);
        ctx.arcTo(w, 0, w, h, r);
        ctx.arcTo(w, h, 0, h, r);
        ctx.arcTo(0, h, 0, 0, r);
        ctx.arcTo(0, 0, w, 0, r);
        ctx.closePath();
        ctx.fill();
      }

      raf = requestAnimationFrame(frame);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    build();
    frame();
    const ro = new ResizeObserver(build);
    ro.observe(host);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <motion.div
      animate={{ opacity: dormant ? 1 : 0 }}
      transition={{
        duration: dormant ? 0.7 : 0.45,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={
        dormant
          ? "absolute inset-0"
          : "pointer-events-none absolute inset-0"
      }
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        aria-hidden="true"
      />

      <motion.div
        animate={{
          opacity: dormant ? 1 : 0,
          y: hovered && dormant ? -4 : 0,
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 flex-col items-center"
      >
        <span className="relative grid h-11 w-11 place-items-center">
          <span className="animate-ring-expand absolute inset-0 rounded-full border border-sun-400/70" />
          <span className="h-2 w-2 rounded-full bg-sun-400 shadow-[0_0_18px_rgba(255,210,31,0.9)]" />
        </span>
      </motion.div>
    </motion.div>
  );
}
